import React, { ReactElement, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useAppSelector } from "redux-store/hooks";

import {
  CustomerContainer,
  HeaderDiv,
} from "pages/customers/styles";
import { CancelButtonSec } from "pages/management/privacy-policy/components/details/styles";
import { SaveButton } from "pages/management/privacy-policy/components/details/styles";
import { WARRANTY } from "constants/route/route-constants";
import { addBrandModelWarranty } from "services/warranty-services";
import { getApiErrorMessage } from "utils/api-error-utils";
import { buildModelCarName, resolveWarrantyPdfForApi } from "utils/warranty-pdf-utils";

import WarrantyPdfPanel from "../components/warranty-pdf-panel";
import { TabContent, WarrantyContainer, ActionButtonsRow } from "../styles";

interface ModelPdfs {
  standard?: string;
  extended?: string;
}

const DEFAULT_WARRANTY_STATUS = "Active";

function AddWarranty(): ReactElement {
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const brandName = useAppSelector((state) => state.brand.brandType);

  const [addModel, setAddModel] = useState("");
  const [modelPdfs, setModelPdfs] = useState<ModelPdfs>({});
  const [modelSaveLoading, setModelSaveLoading] = useState(false);

  useEffect(() => {
    setAddModel("");
    setModelPdfs({});
  }, [brandName]);

  useEffect(() => {
    if (!addModel) {
      setModelPdfs({});
    }
  }, [addModel]);

  const handleModelChange = (model: string) => {
    setAddModel(model);
    setModelPdfs({});
  };

  const handleCancel = () => {
    history.push(WARRANTY);
  };

  const handleSaveModel = async () => {
    if (!addModel) {
      enqueueSnackbar("Please select a car model", { variant: "warning" });
      return;
    }

    const standardWarrantyPdf = resolveWarrantyPdfForApi(modelPdfs.standard, "");
    const extendedWarrantyPdf = resolveWarrantyPdfForApi(modelPdfs.extended, "");

    if (!standardWarrantyPdf && !extendedWarrantyPdf) {
      enqueueSnackbar("Please upload at least one warranty PDF", {
        variant: "warning",
      });
      return;
    }

    setModelSaveLoading(true);
    try {
      await addBrandModelWarranty({
        standardWarrantyPdf,
        extendedWarrantyPdf,
        warrantyStatus: DEFAULT_WARRANTY_STATUS,
        isDefault: false,
        carModel: buildModelCarName(brandName, addModel),
      });
      enqueueSnackbar("Warranty saved successfully", { variant: "success" });
      history.push(WARRANTY);
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, "Failed to save warranty"), {
        variant: "error",
      });
    } finally {
      setModelSaveLoading(false);
    }
  };

  return (
    <CustomerContainer>
      <WarrantyContainer>
        <HeaderDiv />
        <TabContent>
          <WarrantyPdfPanel
            showModelDropdown
            selectedModel={addModel}
            onModelChange={handleModelChange}
            standardPdfUrl={modelPdfs.standard}
            extendedPdfUrl={modelPdfs.extended}
            onStandardUpload={(dataUrl) =>
              setModelPdfs((prev) => ({ ...prev, standard: dataUrl }))
            }
            onExtendedUpload={(dataUrl) =>
              setModelPdfs((prev) => ({ ...prev, extended: dataUrl }))
            }
            onStandardRemove={() =>
              setModelPdfs((prev) => {
                const next = { ...prev };
                delete next.standard;
                return next;
              })
            }
            onExtendedRemove={() =>
              setModelPdfs((prev) => {
                const next = { ...prev };
                delete next.extended;
                return next;
              })
            }
          />
          <ActionButtonsRow>
            <CancelButtonSec
              variant="outlined"
              color="primary"
              onClick={handleCancel}
            >
              Cancel
            </CancelButtonSec>
            <SaveButton
              variant="contained"
              color="primary"
              onClick={handleSaveModel}
              disabled={modelSaveLoading}
            >
              {modelSaveLoading ? "Saving..." : "Save"}
            </SaveButton>
          </ActionButtonsRow>
        </TabContent>
      </WarrantyContainer>
    </CustomerContainer>
  );
}

export default AddWarranty;
